import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useFilters } from "../context/FilterContext";
import { getSetting } from "../lib/api";
import { DEFAULT_CATEGORIES, DEFAULT_MOODS } from "../lib/constants";

export default function FilterSidebar() {
  const {
    open, setOpen,
    category, setCategory,
    collection, setCollection,
    priceMin, setPriceMin,
    priceMax, setPriceMax,
    reset,
    PRICE_DEFAULT_MIN, PRICE_DEFAULT_MAX,
  } = useFilters();
  const location = useLocation();
  const navigate = useNavigate();

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [moods, setMoods] = useState(DEFAULT_MOODS);
  const [localMin, setLocalMin] = useState(priceMin);
  const [localMax, setLocalMax] = useState(priceMax);

  useEffect(() => setLocalMin(priceMin), [priceMin]);
  useEffect(() => setLocalMax(priceMax), [priceMax]);

  // Pull live filter config from admin settings (single source of truth)
  useEffect(() => {
    getSetting("home_categories").then((d) => {
      if (Array.isArray(d?.value) && d.value.length) setCategories(d.value);
    }).catch(() => {});
    getSetting("home_moods").then((d) => {
      if (Array.isArray(d?.value) && d.value.length) setMoods(d.value);
    }).catch(() => {});
  }, []);

  const visibleCats = categories.filter((c) => c.visible !== false);
  const visibleMoods = moods.filter((m) => m.visible !== false);

  const applyPrice = () => {
    setPriceMin(Number(localMin) || PRICE_DEFAULT_MIN);
    setPriceMax(Number(localMax) || PRICE_DEFAULT_MAX);
  };

  const activeCount =
    (category !== "All" ? 1 : 0) +
    (collection !== "All" ? 1 : 0) +
    (priceMin > PRICE_DEFAULT_MIN || priceMax < PRICE_DEFAULT_MAX ? 1 : 0);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            data-testid="filter-backdrop"
          />
          <motion.aside
            initial={{ x: -340, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -340, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="filter-sidebar"
            className="fixed left-0 top-[68px] bottom-0 z-50 w-[320px] sm:w-[340px] bg-[#0A0A0A] border-r border-[#262626] overflow-y-auto"
          >
            <div className="sticky top-0 bg-[#0A0A0A] z-10 border-b border-[#262626] px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span
                  className="font-serif-display"
                  style={{ fontSize: 24, fontWeight: 600, color: "#F5D565", letterSpacing: "0.04em" }}
                >
                  Her Box
                </span>
                {activeCount > 0 && (
                  <span
                    className="text-[11px] font-ui font-semibold rounded-full px-2 py-0.5"
                    style={{ background: "#F5D565", color: "#0A0A0A" }}
                  >
                    {activeCount}
                  </span>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="filter-close-btn"
                className="text-white/70 hover:text-[#F5D565]"
                style={{ minWidth: 44, minHeight: 44, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                aria-label="Close filters"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="px-5 py-5 space-y-7">
              <Section title="Category">
                <div>
                  {visibleCats.map((c) => (
                    <Row
                      key={c.value}
                      label={c.label}
                      active={category === c.value}
                      onClick={() => setCategory(c.value)}
                      testid={`filter-cat-${c.value.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                  ))}
                </div>
              </Section>

              <Section title="Price (₹)">
                <div className="flex gap-3 items-center" style={{ marginTop: 4 }}>
                  <NumInput v={localMin} on={setLocalMin} ph="Min" testid="filter-price-min" />
                  <span className="text-white/40">—</span>
                  <NumInput v={localMax} on={setLocalMax} ph="Max" testid="filter-price-max" />
                </div>
                <button
                  onClick={applyPrice}
                  data-testid="filter-price-apply"
                  className="font-ui mt-3"
                  style={{ fontSize: 13, fontWeight: 600, color: "#F5D565", letterSpacing: "0.05em", minHeight: 44, display: "inline-flex", alignItems: "center" }}
                >
                  Apply →
                </button>
              </Section>

              <Section title="Occasion">
                <div>
                  <Row
                    label="All Occasions"
                    active={collection === "All"}
                    onClick={() => setCollection("All")}
                    testid="filter-coll-all"
                  />
                  {visibleMoods.map((m) => (
                    <Row
                      key={m.value}
                      label={m.label}
                      active={collection === m.value}
                      onClick={() => setCollection(m.value)}
                      testid={`filter-coll-${m.value.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                  ))}
                </div>
              </Section>

              <button
                onClick={() => {
                  // Apply price input box value too (so users who typed but didn't click "Apply →" still get it)
                  applyPrice();
                  setOpen(false);
                  // If user isn't on a product-listing page, take them to /shop with the filters applied
                  if (location.pathname !== "/shop" && location.pathname !== "/") {
                    navigate("/shop");
                  } else if (location.pathname === "/") {
                    navigate("/shop");
                  }
                }}
                data-testid="filter-apply-btn"
                className="font-ui w-full text-center transition-all"
                style={{
                  background: "#F5D565",
                  color: "#0A0A0A",
                  padding: "14px",
                  minHeight: 48,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Apply filters{activeCount > 0 ? ` · ${activeCount}` : ""}
              </button>

              <button
                onClick={reset}
                data-testid="filter-reset-btn"
                className="font-ui w-full text-center transition-all"
                style={{
                  border: "1px solid #262626",
                  padding: "14px",
                  minHeight: 48,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.85)",
                  letterSpacing: "0.08em",
                }}
              >
                Reset all
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div
      className="font-ui"
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: "#F5D565",
        letterSpacing: "1px",
        marginBottom: 12,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const Row = ({ label, active, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full flex items-center justify-between transition-colors"
    style={{
      minHeight: 44,
      padding: "10px 4px",
      color: active ? "#FFFFFF" : "rgba(255,255,255,0.9)",
    }}
  >
    <span className="font-body" style={{ fontSize: 16, fontWeight: 500, letterSpacing: "0.01em" }}>
      {label}
    </span>
    <span className="flex items-center gap-2">
      {active && <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#F5D565" }} />}
      <span
        className="w-3 h-3 rounded-full border"
        style={{
          borderColor: active ? "#F5D565" : "#525252",
          background: active ? "#F5D565" : "transparent",
        }}
      />
    </span>
  </button>
);

const NumInput = ({ v, on, ph, testid }) => (
  <input
    data-testid={testid}
    type="number"
    value={v}
    onChange={(e) => on(e.target.value)}
    placeholder={ph}
    className="font-body w-full bg-transparent border-b border-[#262626] focus:border-[#F5D565] outline-none"
    style={{ fontSize: 16, color: "#FFFFFF", padding: "10px 0", minHeight: 44, fontWeight: 500 }}
  />
);
